/**
 * **按「书 + 表」录入 / 查看**（第五步的核心界面，2026-10-15 用户核对后的改造）。
 *
 * ## 为什么换成这个视角
 *
 * 用户原话：
 *
 * > "一般我录入是按照一本书一本书这样录，**分别录《识字表》《写字表》《词语表》**。
 * >  录《识字表》和《写字表》的字时，我会对字进行造词；录《词语表》时，
 * >  就直接录词语，保存后你就把它关联对应的字。"
 *
 * 改造前的界面是**按课文**组织的（"第 1 课有哪些字"），而家长手上是一本书后面的
 * 三张附录表 —— 那个视角与他的实际动作对不上。这一屏按他说的来：
 *
 *     ① 选书（出版社 + 年级 + 学期）+ 三张表的计数（各多少字/词）
 *     ② 这一张表里有什么 —— **只有家长端有**（只读字卡网格，2026-10-18 恢复）
 *     ③ 点开某一张 → **整块粘贴 → 预览 → 提交**（只有管理员有）
 *
 * ## ② 为什么在**管理员端**删掉、在**家长端**保留（用户原话，别再做反）
 *
 * 上一轮这里被整块删掉了，理由是用户说过"「浏览与编辑」和「录入汉字」两边都有
 * 查询功能……我觉得这重复了"。但用户随后澄清，删的只是**管理员端**那块：
 *
 * > "**我不是说家长端那块字卡要删除**，我说的是**管理员端录入字词的那一块**
 * >  不需要再显示字词了，因为你已经有浏览那一块了吧？"
 *
 * 所以现在的口径是：
 *   · **管理员**（`canEdit === true`）：② **仍然不渲染** —— 他要去「浏览与编辑」
 *     看/改字（那边点字卡能改拼音、造词、标多音字）；
 *   · **家长**（`canEdit === false`）：② **恢复**，用**新网格**
 *     （与「浏览与编辑」同一份 `HanziCard` / `HANZI_GRID_CLASS`）：
 *     拼音在上、字在下、只有已标注的多音字上浅琥珀底色、**卡上零按钮**、
 *     点整张卡 → 浮窗（浮窗里**一个写入口都不渲染**）。
 *
 * ⚠️ 家长端的 ② **不能是"点了没反应"的按钮**：点卡片必须打开
 *    `HanziReadingsModal`（`onOpenHanzi` 由页面传）。
 * ⚠️ 家长端**隐藏读音跟随服务端**：浮窗里的「已隐藏的读音（N）」来自
 *    `hanzi.hidden_readings`（家长看得到管理员藏了哪些，但没有"隐藏/显示"按钮）。
 * ⚠️ 管理员端 ② 删掉之后**不留空洞**：① 的计数卡里给一句
 *    「已在「浏览与编辑」里查看和整理」（那句话也只对管理员说）。
 *
 * ## 「整块粘贴 + 预览 + 幂等」三件能力**原样保留**
 *
 * 这是这个模块能不能用的关键，一步都没丢：
 *   · **整块粘贴**：一张表几百个字一次贴进来（这里只粘**一张表**，所以连表名都不用写）；
 *   · **预览**：`dryRun: true`，**一行都不写**，逐行说出"第 7 行有问题：……"；
 *   · **幂等**：同一份内容贴两次，第二次全部是"已存在、跳过"（收录靠
 *     `uq_hanzi_listing` 挡重复，字与词靠自然键）。
 *
 * ## 三态（本项目反复踩过的那条线）
 *
 *   · 计数 `null`（没拿到）→ 「—」，**不是** 0；
 *   · 列表 `null` → 明说"这份数据没拿到"；
 *   · 一张表真的是 0 条 → 「0 条，还没录」+ 一个"去录入"的入口。
 *
 * ## ⚠️ 2026-10-21（本轮）：预览里那块「⚠ 有问题的行（N）」**整块已删**
 *
 * 用户原话：
 *
 * > "**01 图片中的两块都要去掉，不要！**"
 *
 * 截图里那两段正是三道校验的汇总句（"有 46 个字的读音需要你确认：…" /
 * "有 3 处「同一个字标了同样的音」，不能重复录入：…"），与字卡区里
 * `ImportListingPreview` 的三道校验重复 —— 用户要删，所以**连标题连空壳一起删**，
 * 不再渲染 `report.errors`。
 *
 * ⚠️ 删的只是**渲染**：接口照旧返回 `report.errors`，`summary.errors` 照旧进
 *    统计格「有问题的行」与「确认导入」那句；校验 / 提交闸门一个字都没动。
 */
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Badge, Card, CardHeader, Skeleton } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select } from '../ui/Input';
import { IconCheck, IconChevronDown, IconUpload } from '../ui/Icons';
import { ImportIssueList as IssueList } from './ImportIssueList';
import { DuplicateReport } from './DuplicateReport';
import { ImportListingPreview } from './ImportListingPreview';
import { HANZI_GRID_CLASS, HanziCard } from './HanziCard';
import { WordCardGrid } from './WordCardGrid';
import type { ImportIssueRow } from '../../lib/importIssues';
import {
  describeDistinctTotal,
  describePolyphonicChars,
  describePolyphonicCount,
  GRADES,
} from '@fht/shared';
import { cn } from '../../lib/utils';
import { useImportCounter } from '../../lib/useImportCounter';
import {
  CHAR_FILE_PINYIN_EXAMPLE,
  CHAR_FILE_PINYIN_UNCERTAIN_EXAMPLE,
  CHAR_PINYIN_EXAMPLE,
  HANZI_LIST_PLACEHOLDER,
  WORD_LIST_PLACEHOLDER,
  WORD_PINYIN_EXAMPLE,
} from '../../lib/dictationListExamples';
import {
  ADD_PUBLISHER_VALUE,
  PICK_VOLUME_HINT,
  describePublisherPicker,
  findPublisherVolume,
  publisherSelectOptions,
  resolvePublisherPick,
} from '../../lib/dictationBooksPicker';
import { countLabel } from '../../lib/api/dictation';
import {
  describeLoadedFile,
  readImportFile,
  type ImportFileLoaded,
} from '../../lib/dictationFileUpload';
import type {
  HanziView,
  ImportReportView,
  TextbookGradeCounts,
  TextbookView,
  WordView,
} from '../../lib/api/dictation';

/** 三张表的固定顺序与文案（与 `@fht/shared` 的 `LIST_TYPES` 同口径）。 */
const LIST_TABS = [
  {
    key: 'shizi',
    label: '识字表',
    hint: '课本后面「识字表」里的字，本学期只要求会认、不要求会写。',
  },
  {
    key: 'xiezi',
    label: '写字表',
    hint: '课本后面「写字表」里的字，要求会写。同一个字可以同时在两张表上。',
  },
  {
    key: 'cibiao',
    label: '词语表',
    hint: '课本后面「词语表」里的词。保存后会自动关联到它包含的每一个字。',
  },
] as const;

type ListKey = (typeof LIST_TABS)[number]['key'];

export interface BooksPanelProps {
  textbooks: TextbookView[] | null;
  /** 课本列表本身没拿到（与"还没有课本"是两件事）。 */
  textbooksMissing: boolean;
  loading: boolean;
  /**
   * 能不能维护这张字表。
   *
   * ⚠️ **2026-10-16 起这个 prop 决定"录入那一整张卡片渲不渲染"**，而不是
   *   "按钮灰不灰"。原因：字表维护已经搬到系统管理员的 `/admin/dictation`，
   *   家长端只保留"看"。本项目明确反对"点了会失败 / 灰着的按钮"——
   *   与其渲染一个 disabled 的粘贴框再配一句"你没有编辑权限"，
   *   不如**整个入口不出现**（`canEdit === false` 时下面那些录入相关的 prop
   *   一个都不需要传，所以它们全都是可选的）。
   */
  canEdit: boolean;
  /**
   * 当前选中的**三格 + 表**（由页面持有，切 tab 不丢）。
   *
   * ⚠️ 2026-10-18 本轮：这里不再存 `textbookId` —— 真相是
   *    **出版社 + 年级 + 学期**（三者共同定位一册），那册由
   *    `findPublisherVolume()` 现算。没有对应册时三格照样保留（不自动跳走）。
   */
  selection: { publisher: string; gradeCode: string; semester: string; listType: ListKey };
  onSelect: (patch: Partial<BooksPanelProps['selection']>) => void;
  /** 当前这一册的三张表各有多少（从选中的课本的 `gradeCounts` 里取）。 */
  counts: TextbookGradeCounts | null;
  /**
   * ⚠️ 2026-10-18：`items` 这一组四件套**又回来了** —— 但它们现在**只服务家长端**
   * 的 ②（这一张表里已有什么的字卡网格）。管理员端（`canEdit === true`）仍然
   * 不渲染 ②、页面也**不**为他发这次 `GET .../lists/items`（见 `DictationWorkspace`）。
   *
   * 三态照旧：`null` = **没拿到**（不是"一条都没有"），`[]` = 真的空，
   * `itemsEnabled === false` = **没得查**（这一册还没有课本）。
   */
  /** 这一张表里的字（`shizi` / `xiezi`；`cibiao` 用 `itemsWords`）。`null` = 没拿到。 */
  itemsHanzi?: HanziView[] | null;
  /** 这一张表里的词（`cibiao`）。`null` = 没拿到。 */
  itemsWords?: WordView[] | null;
  /** 这一张表的条数（后端 `total`；`null` = 没拿到）。 */
  itemsTotal?: number | null;
  itemsLoading?: boolean;
  /** 查询失败时的人话（`null` = 没失败）。 */
  itemsError?: string | null;
  /**
   * 这一册能不能查（`false` = 三格没齐 / 书还没建过 → **没发请求**）。
   *
   * ⚠️ 它必须与"查了但没拿到"分开说：前者是"这一册还没有字表"，
   *    后者是"数据没拿到，请刷新"。
   */
  itemsEnabled?: boolean;
  onRetryItems?: () => void;
  /**
   * 下面这一组**只有 `canEdit === true` 时才需要**（录入卡片里的东西）：
   * 粘贴的内容、预览结果、提交结果。只读浏览时**不要传**。
   */
  /** 粘贴的内容（一张表一份，由页面持有，切表不丢）。 */
  text?: string;
  onText?: (value: string) => void;
  /** 预览结果（`dryRun` 那份）。 */
  preview?: ImportReportView | null;
  previewError?: string | null;
  /**
   * 预览**失败**时后端 `error.details` 里的逐条原因（行号 + 词元 + 原文 + 原因）。
   *
   * ⚠️ 用户实测（2026-10-16）：失败时界面只有一句"预览失败：提交的内容有误"，
   *    看不出哪一行、哪个词元有问题。`previewError` 现在已经是**具体那句**
   *    （见 `describeApiFailure`），这里是**全部**细节，由这个列表显示出来。
   */
  previewIssues?: readonly ImportIssueRow[];
  previewing?: boolean;
  /** 提交结果。 */
  commit?: ImportReportView | null;
  commitError?: string | null;
  committing?: boolean;
  onPreview?: () => void;
  onCommit?: () => void;
  onReset?: () => void;
  /** 打开某个字的"读音与造词"（多音字按音造词）。**读操作，两种模式都有。** */
  onOpenHanzi?: (item: HanziView) => void;
  /**
   * 打开「添加出版社」**浮窗**（入口是出版社下拉的最后一项）—— **只有维护模式传**。
   *
   * 选中下拉里的那个动作项时**不改任何选择值**，只调这一个回调。
   *
   * ⚠️ 浮窗里**只有出版社名**一格；年级 / 学期用页面这两格（它们始终可选）。
   * ⛔ 页面上既没有「＋ 新建课本（出版社 + 年级 + 学期）」那块，
   *    也**没有**任何"建一册"的入口 —— 用户不需要这个概念：
   *    三格选好就能录，**保存时后端自动建出这本书**。
   */
  onAddPublisher?: () => void;
  /**
   * **预览里给重复出现的字选读音**（迁移 0018）。
   *
   * 只传"选好了"：父组件把它加进下一次预览请求并重新预览；这里**不发请求**。
   */
  onPickReading?: (line: number, index: number, reading: string) => void;
  /** 正在重新预览（选音之后）；按钮据此禁用。 */
  readingsBusy?: boolean;
}

/** 一行统计：数字拿不到时显示「—」，不显示 0。 */
function Stat({
  label,
  value,
  tone = 'slate',
}: {
  label: string;
  value: number | null;
  tone?: 'slate' | 'emerald' | 'amber' | 'rose';
}) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    rose: 'bg-rose-50 text-rose-700',
  } as const;
  return (
    <div className={cn('rounded-xl px-3 py-2', tones[tone])}>
      <p className="text-xs opacity-80">{label}</p>
      <p className="text-lg font-bold tabular-nums">{countLabel(value)}</p>
    </div>
  );
}

/** 某一册的"三张表各多少"——**收录数为 0 的表也要显示出来**（那才是最该去录的）。 */
function countsOf(counts: TextbookGradeCounts | null, key: ListKey): number | null {
  if (counts === null) return null;
  const value = counts.counts[key];
  return typeof value === 'number' ? value : null;
}

export function DictationBooksPanel(props: BooksPanelProps) {
  const {
    textbooks,
    textbooksMissing,
    loading,
    canEdit,
    selection,
    onSelect,
    counts,
    /**
     * ⚠️ 这一组只在 `canEdit === true` 时才有意义，所以**给了默认值**：
     *   只读浏览时页面一个都不传，下面那张录入卡片也整个不渲染。
     */
    text = '',
    onText,
    preview = null,
    previewError = null,
    previewIssues = [],
    previewing = false,
    commit = null,
    commitError = null,
    committing = false,
    onPreview,
    onCommit,
    onReset,
    onOpenHanzi,
    onAddPublisher,
    onPickReading,
    readingsBusy = false,
    /** ② 那一块（**只有家长端**用）。全部给默认值：管理员端一个都不传。 */
    itemsHanzi = null,
    itemsWords = null,
    itemsTotal = null,
    itemsLoading = false,
    itemsError = null,
    itemsEnabled = false,
    onRetryItems,
  } = props;

  const [formatOpen, setFormatOpen] = useState(false);

  /**
   * **上传文件**（2026-10-20）—— 只做"把文件读成文本、填进粘贴框"这一件事。
   *
   * 用户原话（背景）："每次拿到一本语文书，会把后面的识字表 / 写字表 / 词语表拍照，
   * 让助手生成一个**文本文件**，然后**上传到系统**录入。"
   *
   * ⚠️ 所以它**不改**粘贴 / 预览 / 导入那条流程：内容照样落进下面那个 textarea，
   *    用户看得见、能改、能核对，点「预览」仍然是原来那一步。
   * ⚠️ 文件名要显示出来（用户点名）：`已载入：一年级下册识字表.txt`。
   * ⚠️ 读失败时**不碰粘贴框**（不把空内容/乱码填进去覆盖用户已有的东西）。
   */
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [loadedFile, setLoadedFile] = useState<ImportFileLoaded | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileBusy, setFileBusy] = useState(false);

  /** 内容被清空（「清空重来」/ 自己删光）⇒ "已载入"那行也要跟着消失。 */
  useEffect(() => {
    if (text.trim() === '') setLoadedFile(null);
  }, [text]);

  /** 切表 / 换册 ⇒ 上一份文件的提示作废。 */
  useEffect(() => {
    setLoadedFile(null);
    setFileError(null);
  }, [selection.listType]);

  const handleFilePicked = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const input = event.currentTarget;
    const file = input.files?.[0] ?? null;
    /** 允许连续选同一个文件：清掉 input 的值（File 对象已经拿在手里了）。 */
    input.value = '';
    if (file === null) return;

    setFileError(null);
    setFileBusy(true);
    try {
      const result = await readImportFile(file);
      if (!result.ok) {
        setFileError(result.rejection.message);
        return;
      }
      setLoadedFile(result.file);
      /**
       * ⚠️ 走页面同一个 `onText`：它会**作废预览 / 选音**（提交必须用看过的那一份），
       *    所以上传之后一定要重新点「预览」。
       */
      onText?.(result.file.text);
    } finally {
      setFileBusy(false);
    }
  };

  /**
   * 「出版社」这一格现在是什么情况（**纯函数判定，有单测**）。
   *
   * ⚠️ 一条课本都没有时，那行小字与就地指引必须点名 **「＋ 添加出版社」**
   *    （下拉最后一项）—— 那是空部署里唯一能建第一册的入口。
   *    旧文案指的是一块已经删掉的「＋ 新建课本（出版社 + 年级 + 学期）」，不能留。
   */
  const publisherPicker = describePublisherPicker({
    textbooks,
    loading,
    missing: textbooksMissing,
    canEdit,
  });

  /**
   * 三格齐全没有（齐了就能录：**书存不存在不影响**）。
   *
   * ⚠️ 年级 / 学期下拉里保留了空值项（"请选择年级 / 请选择学期"）：空值 = 还没选好，
   *    **不是**"第一册"。
   * ⚠️ 三格齐了但书还没建过时：三张表照常显示（计数 0）、预览 / 导入照常可用，
   *    保存时后端自动建出这本书并把字表归到它下面。
   */
  const volumeQueryComplete =
    selection.publisher !== '' && selection.gradeCode !== '' && selection.semester !== '';

  /**
   * 三格定位到的那一册（`null` = 书还没建过 —— 但**照常能录**）。
   *
   * ⚠️ 不拿"这家出版社的第一册"兜底：那会把下册的字录进上册。
   */
  const activeTextbook = useMemo(
    () => (volumeQueryComplete ? findPublisherVolume(textbooks, selection) : null),
    [volumeQueryComplete, textbooks, selection.publisher, selection.gradeCode, selection.semester],
  );

  /** 下拉的受控值 = **选择状态里的出版社**（不是用户点过的动作项）。 */
  const selectedPublisher = selection.publisher;

  /** 下拉选项：占位项 → 去重后的出版社 → 维护模式最后一项「＋ 添加出版社」。 */
  const publisherOptions = useMemo(
    () =>
      publisherSelectOptions(textbooks, {
        placeholder: publisherPicker.placeholder,
        canAdd: canEdit,
      }),
    [textbooks, publisherPicker.placeholder, canEdit],
  );

  /**
   * 换出版社 / 选中最后那个动作项。
   *
   * ⚠️ 「＋ 添加出版社」是**动作**，不是值：遇到它只调 `onAddPublisher`，
   *    **不碰** `onSelect`。下拉的受控值仍是当前选的那家（见 `selectedPublisher`）。
   * ⚠️ 这里顺手把 DOM 的 `value` 拨回去：即使父组件因为某种原因没有立刻重渲染，
   *    显示值也不会停在"＋ 添加出版社"上。
   */
  const changePublisher = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.currentTarget.value;
    if (value === ADD_PUBLISHER_VALUE) {
      event.currentTarget.value = selectedPublisher;
      onAddPublisher?.();
      return;
    }
    const pick = resolvePublisherPick(textbooks, value);
    if (pick.kind === 'publisher') {
      /** 只换出版社：年级 / 学期**不动**，由三格一起决定是哪一册。 */
      onSelect({ publisher: pick.publisher });
    } else if (pick.kind === 'clear') {
      /** 选回占位项：只清出版社；年级 / 学期保留（它们始终可选）。 */
      onSelect({ publisher: '' });
    } else if (pick.kind === 'unknown') {
      event.currentTarget.value = selectedPublisher;
    }
  };
  const activeTab = LIST_TABS.find((tab) => tab.key === selection.listType) ?? LIST_TABS[0];

  const report = commit ?? preview;
  const isWordList = selection.listType === 'cibiao';
  /**
   * **保存前的三道校验**（迁移 0018）：字表必须"拿到了校验结果、而且 ok"。
   *
   * ⚠️ `null` = 老后端没给 → **不能**当作通过（用户明确："检查没问题的，你才要保存"）。
   * ⚠️ 词语表没有"每个字一个位置"这件事：`readings.positions` 为空、ok 为 true。
   */
  const readingsCheck = preview?.readings ?? null;
  const readingsOk = isWordList ? true : readingsCheck !== null && readingsCheck.ok;
  /** 要提交的那一份内容**从预览那一份读**（输入改了就必须重新预览）。 */
  const canCommit =
    preview !== null &&
    commit === null &&
    !committing &&
    !previewing &&
    preview.summary.created !== null &&
    readingsOk;

  /**
   * 粘贴框右下角的计数器（`lib/importCounter.ts`，与预览**同一个解析器**）。
   *
   * ⚠️ 改造前这里是 `text.length` 个字符：用户那段 33 行 / 284 个汉字的内容显示成
   *    568 个字符，而上面预览写「生字 284 条」—— 两个数对不上就没法核对。
   *    现在：**汉字 284 个（共 568 个字符，含空格/换行）**。
   */
  const counter = useImportCounter({
    text,
    listType: selection.listType,
    defaultTextbook: activeTextbook?.id ?? '',
  });

  return (
    <div className="space-y-4">
      {/* ============================================ ① 选书 + 选表 */}
      <Card>
        <CardHeader
          title="① 选一本书、一张表"
          description="照课本后面那三张附录表录：识字表、写字表、词语表，各录各的。"
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {/*
            ---------- ① 出版社（2026-10-18 用户要求：原来叫「课本版本」）----------
            ⚠️ 选项**只有出版社名**（`listPublisherOptions` 去重 + 排序）：
               ⛔ 不要带年级 / 学期 / 字数统计（用户原话"只显示出版社"）。
            ⚠️ 最后一项是**动作**「＋ 添加出版社」：选中它不改任何值，只打开浮窗
               （见 `changePublisher`；显示值由 `selectedPublisher` 受控，不会停在它上面）。
            ⚠️ 一条课本都没有时下拉**不禁用**（维护模式）：最后那一项是建第一册的唯一入口。
          */}
          <Select
            label="出版社"
            value={selectedPublisher}
            disabled={publisherPicker.disabled}
            onChange={changePublisher}
            hint={publisherPicker.hint ?? undefined}
          >
            {publisherOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          {/*
            ---------- 年级 / 学期（**预设值，始终可选**）----------
            ⚠️ 2026-10-18 本轮用户定稿：年级是"小学一年级 ~ 小学六年级"、学期是
                "上册 / 下册"这些**预设值**，⛔ 没有"新建年级 / 新建学期"任何入口。
            ⚠️ 两格**永远可编辑**（不绑死在书上）—— 否则"同一家出版社只有上册"时
                用户切不到下册。
            ⚠️ 三格（出版社 + 年级 + 学期）一起决定是哪一册：换学期就是换一册。
                **书还没建过也照常能录**（保存时后端自动建），所以这里没有任何
                "缺书"提示。
          */}
          <Select
            label="年级"
            value={selection.gradeCode}
            onChange={(event) => onSelect({ gradeCode: event.target.value })}
            hint={PICK_VOLUME_HINT}
          >
            <option value="">请选择年级</option>
            {GRADES.map((grade) => (
              <option key={grade.code} value={grade.code}>
                {grade.label}
              </option>
            ))}
          </Select>
          <Select
            label="学期"
            value={selection.semester}
            onChange={(event) => onSelect({ semester: event.target.value })}
            hint={PICK_VOLUME_HINT}
          >
            <option value="">请选择学期</option>
            <option value="上">上册</option>
            <option value="下">下册</option>
            <option value="不分">不分学期</option>
          </Select>
        </div>

        {/*
          ---------- 就地指引：一条课本都没有 ----------
          ⚠️ 必须在**下拉的紧邻处**（而不是页面最下面那张卡片）。
          ⚠️ 这句话**只提「＋ 添加出版社」**（下拉最后一项）：旧的
             「＋ 新建课本（出版社 + 年级 + 学期）」那块已经按用户要求删除，
             再提它就是叫人去找一个不存在的东西；「去整块导入」同理由此不再出现。
        */}
        {publisherPicker.guide !== null && (
          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-sm text-amber-900">{publisherPicker.guide.message}</p>
          </div>
        )}

        {/*
          ⚠️ 这里**没有**"这一册还没有"的提示，也没有任何"建一册"入口
             （2026-10-18 本轮用户定稿："三格选好就可以录入了"）：
             三格齐了就照常能粘、能预览、能导入，保存时后端自动建出这本书。
          ⚠️ 原来这里有一排"这本书已经录过的册"快捷按钮 —— 一本书 = 一册之后
             它没有意义了（每本书只有自己那一册），已删掉。
        */}

        {/* ---------- 三张表（并排，各带计数） ---------- */}
        {/*
          ⚠️ 2026-10-18 本轮：这排卡片在**维护模式**下是"我现在要录哪张表"的切换器
             （点它会换 `selection.listType`，③ 跟着换）。
             在**只读模式**（家长）下它只是"这一册三张表各有多少"的展示 ——
             那就不该是一个按钮：点了没有任何反应的按钮 = 教人去点一个坏掉的东西。
             所以家长那边渲染成 `<div>`（同样的样式，没有 `aria-pressed` / 没有 hover）。
        */}
        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {LIST_TABS.map((tab) => {
            const count = countsOf(counts, tab.key);
            const active = canEdit && tab.key === selection.listType;
            const content = (
              <>
                <span className="flex items-baseline justify-between gap-2">
                  <span className={cn('font-semibold', active ? 'text-brand-800' : 'text-slate-800')}>
                    {tab.label}
                  </span>
                  <span className="shrink-0 text-sm tabular-nums text-slate-500">
                    {countLabel(count)}
                    {/*
                      ⚠️ 量词看**这一张表自己**（`tab.key`），不看当前选中的那张表：
                         改造前写的是 `isWordList && tab.key === 'cibiao'` —— 选中词语表时
                         识字表 / 写字表会显示「N 个词」，反过来词语表显示「N 个字」。
                    */}
                    {tab.key === 'cibiao' ? ' 个词' : ' 个字'}
                  </span>
                </span>
                <span className="mt-0.5 block text-[11px] text-slate-500">{tab.hint}</span>
              </>
            );
            const boxClass = cn(
              'min-w-0 rounded-xl border px-3 py-2 text-left',
              active
                ? 'border-brand-400 bg-brand-50'
                : 'border-slate-300 bg-white',
            );
            return canEdit ? (
              <button
                key={tab.key}
                type="button"
                onClick={() => onSelect({ listType: tab.key })}
                aria-pressed={active}
                className={cn(boxClass, 'hover:bg-slate-50')}
              >
                {content}
              </button>
            ) : (
              <div key={tab.key} className={boxClass}>
                {content}
              </div>
            );
          })}
        </div>

        {/*
          ---------- 指路：这一册里已经录了什么，去「浏览与编辑」看 ----------
          ⚠️ 2026-10-18（本轮修正）：这张卡片原来被整块删掉，于是补了这句指路
             （用户原话要求的那句「已在「浏览与编辑」里查看和整理」）。
             现在 ② 在**家长端**恢复了（见下面那段），所以这句指路**只对管理员**说 ——
             家长眼前就有字卡，再叫他去别的 tab 等于把刚恢复的东西又说没了。

          ⚠️ 只在三格齐了时出现：没选书的时候说"这一册"是空的（还没确定是哪一册）。
        */}
        {canEdit && volumeQueryComplete && (
          <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
            这一册已经录了哪些字，<strong>已在「浏览与编辑」里查看和整理</strong>
            —— 点字卡就能改拼音、造词、标多音字。
          </p>
        )}

        {activeTextbook !== null && counts === null && (
          <p className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            这本书的收录数量没拿到（不是"一条都没录"）—— 请刷新重试。
          </p>
        )}
        {selection.publisher === '' && (
          <p className="mt-3 text-sm text-slate-500">
            先选出版社 —— 出版社、年级、学期三格就是这一本书；还没有的话，保存时会自动建好。
          </p>
        )}
      </Card>

      {/*
        ============================================ ② 这一张表里有什么（**只有家长端**）

        用户原话（2026-10-18，上一轮删错之后）：

        > "**我不是说家长端那块字卡要删除**，我说的是**管理员端录入字词的那一块**
        >  不需要再显示字词了，因为你已经有浏览那一块了吧？"

        所以：
          · **管理员**：这块**仍然不渲染**（他要去「浏览与编辑」改字；上面那句
            指路只对他出现）。页面也**不**为他发 `GET .../lists/items`；
          · **家长**：这块**恢复**，而且用**新网格**（与「浏览与编辑」同一份
            `HanziCard` / `HANZI_GRID_CLASS`）：拼音在上、字在下、
            只有已标注的多音字上浅琥珀底色、卡上零按钮、点卡 → 浮窗。

        ⚠️ 卡上**真的没有按钮**，但点整张卡**必须**有反应（`onOpenHanzi`）——
            用户点名"恢复后家长端那块不能是点了没反应的按钮"。
        ⚠️ 浮窗里家长**只读**：标多音字 / 改拼音 / 编辑 / 删除 / 隐藏读音 / 造词
            一个都不渲染（见 `HanziReadingsModal` 的 `canEdit` 与各回调）。
        ⚠️ 三态：`itemsEnabled === false`（这一册还没有课本）≠ 查询失败 ≠ 真的空。
      */}
      {!canEdit && volumeQueryComplete && (
        <Card>
          <CardHeader
            title={`② 这一张表里有什么（${activeTab.label}）`}
            description={
              isWordList
                ? '这一册「词语表」里已经录进去的词。点词不会跳走 —— 这里只看。'
                : '拼音在上、字在下。点任意一个字 → 弹出浮窗，看它的每个读音各有哪些词；琥珀色底色的是管理员确认标过的多音字。'
            }
            actionAlign="start"
            action={
              <Badge tone={itemsTotal === null && itemsEnabled ? 'warn' : 'neutral'}>
                共 {countLabel(itemsTotal)} {isWordList ? '个词' : '个字'}
              </Badge>
            }
          />

          {!itemsEnabled ? (
            /*
              ⚠️ 这一册**还没有课本**（"出版社 + 年级 + 学期"没有对应的书）：
                  这不是错误，也不是"0 条" —— 就是还没有可看的东西。
                  管理员把字表录进来之后，这里自然就有内容。
            */
            <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
              这一册还没有录入{activeTab.label} —— 站点管理员把字表录进来之后，这里就能点开看每个字的读音与词。
            </p>
          ) : itemsError !== null ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              没拿到这一张表的内容：{itemsError}
              {onRetryItems !== undefined && (
                <button type="button" onClick={onRetryItems} className="ml-2 font-medium underline">
                  重试
                </button>
              )}
            </div>
          ) : itemsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : isWordList ? (
            itemsWords === null ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                没拿到这一张表的词（不是"一个词都没有"）—— 请刷新重试。
              </p>
            ) : itemsWords.length === 0 ? (
              <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
                这一册的词语表还没有录词。
              </p>
            ) : (
              <ul className="space-y-1.5">
                {itemsWords.map((word) => (
                  <li
                    key={word.id}
                    className="flex flex-wrap items-baseline gap-2 rounded-xl border border-slate-200 px-3 py-1.5"
                  >
                    <span className="font-mono text-xs text-slate-500">{word.pinyin}</span>
                    <span className="text-base font-semibold text-slate-900">{word.wordText}</span>
                  </li>
                ))}
              </ul>
            )
          ) : itemsHanzi === null ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              没拿到这一张表的字（不是"一个字都没有"）—— 请刷新重试。
            </p>
          ) : itemsHanzi.length === 0 ? (
            <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
              这一册的{activeTab.label}还没有录字。
            </p>
          ) : (
            <>
              {itemsTotal !== null && itemsTotal > itemsHanzi.length && (
                <p className="mb-2 text-xs text-slate-500">
                  一共 {itemsTotal} 个字，这里显示前 {itemsHanzi.length} 个。
                </p>
              )}
              <ul className={HANZI_GRID_CLASS}>
                {itemsHanzi.map((item) => (
                  /* ⚠️ 同一个字录了 N 次 = N 行：`id` 相同、`listingId` 不同。 */
                  <HanziCard
                    key={item.listingId ?? item.id}
                    item={item}
                    onOpen={() => onOpenHanzi?.(item)}
                  />
                ))}
              </ul>
            </>
          )}
        </Card>
      )}

      {/*
        ---------- ③ 整块粘贴 → 预览 → 提交 ----------

        ⚠️ **`canEdit === false` 时整张卡片不渲染**（不是禁用）。
        录入是系统管理员的事（2026-10-16 从家长端搬走），家长端的这一页
        根本不该出现粘贴框 —— 本项目明确反对"点了会失败 / 灰着的按钮"。
        ⚠️ 2026-10-18 本轮：**三格齐了就能录**（不再要求"书已经建好"）。
           书还没建过时照样能贴、能预览、能导入；保存时后端自动建出这本书。
      */}
      {canEdit &&
        volumeQueryComplete && (
        <Card>
          <CardHeader
            title="③ 录这一张表（整块粘贴）"
            description={
              isWordList
                ? '一行一个词就行（也可以从 Excel 整列粘）。保存后会自动关联到这些词里的每一个字，并归到「出版社 + 年级 + 学期」这本书上。'
                : '照课本后面那张表整块粘进来：一行一个字、或者按表格粘贴都认。粘完先点「预览」；保存时会归到「出版社 + 年级 + 学期」这本书上（还没有就当场建好）。'
            }
          />

          {/*
            ⚠️ 书还没建过时，把"保存时会自动建"这件事**说在前头**（中性提示，不是警告）：
              用户不必先去找一个"建这一册"的按钮（那个入口已经按用户要求删掉了）。
          */}
          {activeTextbook === null && (
            <p className="mb-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
              这本书还没建过 —— 不用先建：保存时会自动建好「出版社 + 年级 + 学期」这一册，
              并把这次录的字表归到它下面。
            </p>
          )}

          <label htmlFor="dictation-list-text" className="mb-1 block text-sm font-medium text-slate-700">
            内容
          </label>

          {/*
            ---------- 上传文件（.txt / .csv）：只把它**填进下面这个框**（2026-10-20）----------

            用户原话："每次拿到一本语文书……让助手生成一个**文本文件**，然后**上传到系统**录入。"
            所以这个入口只做"读成文本、填进框"：**不解析、不导入、不跳过预览** ——
            内容落在 textarea 里，用户逐行核对完再点「预览」（这是他自己要求的那一步）。

            ⚠️ 文案是用户点名要的（`📄 上传文件（.txt / .csv）`），别改。
            ⚠️ 文件名必须显示出来（`已载入：一年级下册识字表.txt`）—— 他要知道
               刚才选的是哪一份文件。
            ⚠️ 三种坏文件（太大 / 不是 UTF-8 / 空文件）的提示在 `lib/dictationFileUpload.ts`
               里统一给（有单测），这里只负责显示那一句人话。
          */}
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={fileBusy || committing || previewing}
            >
              📄 上传文件（.txt / .csv）
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.csv,text/plain,text/csv"
              className="hidden"
              aria-label="上传识字表 / 写字表 / 词语表的文本文件"
              onChange={(event) => void handleFilePicked(event)}
            />
            <span className="text-xs text-slate-500">
              {fileBusy
                ? '正在读取文件…'
                : '选文件 → 读成文本 → 填进下面这个框（不会直接导入，还要点「预览」核对）'}
            </span>
          </div>

          {loadedFile !== null && (
            <p className="mb-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {describeLoadedFile(loadedFile)}
            </p>
          )}

          {fileError !== null && (
            <p role="alert" className="mb-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {fileError}
            </p>
          )}

          <textarea
            id="dictation-list-text"
            value={text}
            onChange={(event) => onText?.(event.target.value)}
            spellCheck={false}
            rows={10}
            placeholder={isWordList ? WORD_LIST_PLACEHOLDER : HANZI_LIST_PLACEHOLDER}
            className={cn(
              'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-sm leading-6 text-slate-900',
              'placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
              'min-h-[12rem]',
            )}
          />
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            {/* 识别出的字/词元数（与预览里「生字 N 条」同口径）+ 字符数（含空格/换行）。 */}
            <span>{counter.label}</span>
            <button
              type="button"
              onClick={() => setFormatOpen((prev) => !prev)}
              className="flex items-center gap-1 font-medium text-brand-600 hover:underline"
            >
              <IconChevronDown size={14} />
              {formatOpen ? '收起格式说明' : '格式说明'}
            </button>
          </div>

          {formatOpen && (
            <div className="mt-2 space-y-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <p>
                <strong className="text-slate-800">字表</strong>：一行一个字、或者一行里用空格/顿号
                排开都行；也能直接贴 Excel 里的几列。
              </p>
              <p>
                <strong className="text-slate-800">顺手造词</strong>：写
                <code className="mx-1 rounded bg-white px-1">天 天地</code>
                （第二个是词，中间用<strong>空格</strong>）—— 效果与在词语表里录一样，
                会自动关联到这个字。
              </p>
              <p>
                <strong className="text-slate-800">要指定读音</strong>
                ：<strong>整份都按两列写、第一行是表头</strong>，列之间用逗号
                （拼音本身含空格，所以不能用空格分列）：
              </p>
              <pre className="overflow-x-auto rounded-lg bg-white px-3 py-2 font-mono text-xs leading-5 text-slate-700">
                {CHAR_PINYIN_EXAMPLE}
              </pre>
              <p>词语表里的词同理（多音字组成的词一定要写整词拼音）：</p>
              <pre className="overflow-x-auto rounded-lg bg-white px-3 py-2 font-mono text-xs leading-5 text-slate-700">
                {WORD_PINYIN_EXAMPLE}
              </pre>
              <p>
                <strong className="text-slate-800">上传的文件</strong>也认这种「字,拼音」
                —— 而且<strong>一行可以放很多个</strong>（一行 = 课本上的一行，词元之间用
                Tab 或空格）：
              </p>
              <pre className="overflow-x-auto rounded-lg bg-white px-3 py-2 font-mono text-xs leading-5 text-slate-700">
                {CHAR_FILE_PINYIN_EXAMPLE}
              </pre>
              <p className="rounded-lg bg-sky-50 px-3 py-2 text-sky-900">
                文件里<strong>给了拼音 = 这个位置已经标音</strong>：<code>霜,shuāng</code> 就存成
                「霜」在这个位置的读音（预览里的字卡上显示的就是它），
                所以<strong>重复出现的字不用再手工标音</strong>；没写拼音的词元照旧用默认读音。
                <br />
                ⚠️ <strong>单音字的读音一律以字典为准</strong>（文件里写错了也按字典落库）；
                只要<strong>文件里写的拼音和字典不一样</strong>，预览里就标
                <strong>浅橙色</strong>等你确认（多音字仍然用文件写的那个音，同样标出来核对）。
                拼音可以写成 <code>shuāng</code>、<code>shuang1</code> 或 <code>shuang</code>
                三种；写错了会<strong>逐行报错</strong>（不会静默录错音）。
              </p>
              <p className="rounded-lg bg-orange-50 px-3 py-2 text-orange-900">
                <strong>不确定的读音标一个「?」</strong>（半角 <code>?</code>、全角{' '}
                <code>？</code> 都行）：写成 <code>得,dé?</code> —— 意思是「这个音我不确定」。
                <code>?</code> 不是拼音的一部分，录入时会被剥掉、只记 <code>dé</code>；
                预览里这些字会标<strong>浅橙色</strong>，你点它 → 浮窗里点
                <strong>「✅ 确认就是「dé」」</strong>（音对）或换一个读音（音不对），
                确认之后底色变<strong>浅绿色</strong>。
                <strong>带「?」还没确认的字不能保存</strong>（会提示「有 N 个字的读音需要你确认」）。
                <br />
                另外，<strong>文件里写的拼音和字典不一样</strong>的位置也标浅橙（文案会写出
                文件里写的音和字典默认的音），同样要你对着课本确认之后才能保存。
                例：
              </p>
              <pre className="overflow-x-auto rounded-lg bg-white px-3 py-2 font-mono text-xs leading-5 text-slate-700">
                {CHAR_FILE_PINYIN_UNCERTAIN_EXAMPLE}
              </pre>
              <p>
                <strong className="text-slate-800">词语表</strong>：一行一个词就行。
              </p>
              {/*
                ⚠️ 这段说明里的每条示例都在 `lib/dictationListExamples.ts`，
                   并由那个文件的单测拿**真实解析器**逐条跑过 —— 改造前这里写着
                   "给字注音：写 长 cháng"，而那个写法解析器根本不认（报"字列必须是
                   单个汉字"）。示例与解析器脱钩时，测试会先失败。
              */}
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-800">
                ⚠️ 重复导入不会产生重复数据：同一个字在同一册同一张表上只会有一条收录；
                已经存在的会显示为「已存在、跳过」。
              </p>
            </div>
          )}

          {/*
            ---------- 顺序（2026-10-19 用户明确要求） ----------
            · 同一册同一张表里，`sort_order` 现在是**逐字序号**（粘贴时的字符顺序）；
            · 历史数据是按「原文行号」写的（一行多个字共享一个号），行内先后已经丢失
              —— **整表重贴一次**才能补正。
              只贴一部分时：新字接在表尾，已经录过的字保持原来的相对顺序
              （重复粘到的字不会把新字带到表中间）。
            ⚠️ 这段话是界面上唯一的操作说明：用户要照着它去修历史数据。
          */}
          {!isWordList && (
            <p className="mt-2 rounded-xl bg-sky-50 px-3 py-2 text-sm text-sky-900">
              <strong>顺序</strong>：同一册同一张表里的字，按你<strong>粘贴时的字符顺序</strong>
              编号（第 1 个字 = 1、第 2 个 = 2……）。
              旧数据是按「原文行号」记的（同一行的几个字分不出先后）——
              把<strong>整张表原样再粘一次</strong>并确认导入，就会按这次的顺序补正
              （<strong>不会重复建字</strong>）。
              只粘一部分时：新字接在表尾，已经录过的字保持原来的先后顺序
              （重复粘到的字不会把新字带到表中间）；要整表对齐，请一次性粘完整张表。
            </p>
          )}
          {/*
            ⚠️ 2026-10-21 删掉了这里原来那句「词语表目前按入库先后显示；按粘贴顺序重排
               需要给词语新增一列『顺序』（要数据库迁移），暂未启用。」
               —— 它已经**不成立**：迁移 0017 起 `word.sort_order` 就是逐词序号，
               导入时按粘贴顺序编号、列表也按它升序（见 `dictation.service.ts` 里
               "册内 `word.sort_order` 升序"那段）。留着只会让人以为要等一次迁移。
          */}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              loading={previewing}
              disabled={committing || text.trim() === ''}
              onClick={() => onPreview?.()}
              icon={<IconUpload size={18} />}
            >
              预览（不会写入任何数据）
            </Button>
            {(text !== '' || report !== null) && (
              <Button variant="secondary" onClick={() => onReset?.()} disabled={committing}>
                清空重来
              </Button>
            )}
          </div>
          {previewError !== null && (
            <p className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              预览失败：{previewError}
            </p>
          )}
          {/*
            ⚠️ **失败时的逐条原因也要显示**（2026-10-16 用户实测："报错太笼统，
               看不出哪一行、哪个词元有问题"）。
            后端把真正的原因放在 `error.details` 里（`{field,reason}`、zod 的
               `[{path,message}]`、导入的 `[{line,raw,token,message}]`），
               改造前界面只读 message 那一句通用文案 ⇒ details 被整块丢掉。
            现在：message 换成具体那句（`describeApiFailure` 那层的修复），
               并且把 details 逐条列出来（这里）。
          */}
          {previewIssues.length > 0 && (
            <div className="mt-2">
              <IssueList title="后端说的具体原因" issues={previewIssues} tone="error" />
            </div>
          )}
        </Card>
      )}

      {/* ============================================ 预览 / 提交结果 */}
      {report !== null && (
        <Card>
          <CardHeader
            title={commit !== null ? '导入结果' : '预览结果（还没有写入任何数据）'}
            description={
              commit !== null
                ? '下面是这次真正写入的结果。'
                : '看清下面这些行，确认没问题再点最下面的「确认导入」。'
            }
            actionAlign="start"
            action={
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="neutral">{activeTab.label}</Badge>
                {report.parse.header.length > 0 && (
                  <Badge tone="neutral">识别到表头 {report.parse.header.join(' / ')}</Badge>
                )}
              </div>
            }
          />

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <Stat label="新增" value={report.summary.created} tone="emerald" />
            <Stat label="更新" value={report.summary.updated} tone="amber" />
            <Stat label="已存在、跳过" value={report.summary.skipped} />
            <Stat label="有问题的行" value={report.summary.errors} tone="rose" />
            <Stat
              label="写入的收录"
              value={commit !== null ? (commit.links?.hanziListing ?? null) : null}
            />
          </div>

          {/*
            ---------- 原文行数（只留一组数字） ----------
            ⚠️ 2026-10-21：改造前这一行是
               「共解析 {summary.rows} 行数据（原文 N 行，空行 M；生字 K 条、词语 W 条）」
               —— `summary.rows` 是**去重后**要写入的行数（多音字重复会被合并），
               而 `parse.charRows` 是**解析出来的**字数，两个数在同一句里并排
               （407 行 vs 生字 410 条）看着就像算错了。
               现在只说"原文几行、空行几行 + 这一张表的条数"，去重后的口径交给
               下面那块「一共 N 个字（多音字只算一次）」和字卡网格的标题去说。
          */}
          <p className="mt-2 text-xs text-slate-500">
            原文 {countLabel(report.parse.totalLines)} 行，空行{' '}
            {countLabel(report.parse.blankLines)}；
            {isWordList
              ? `词语 ${countLabel(report.parse.wordRows)} 条`
              : `生字 ${countLabel(report.parse.charRows)} 条`}
            。
          </p>

          {/*
            ---------- 用户点名要的两个数（2026-10-17） ----------
            · 一共几个字（多音字只算一次）= 实际会留在字表里的行数口径；
            · 多音字有几个 + 把它们列出来。
            ⚠️ 文案走 shared 的 `describeDistinctTotal` / `describePolyphonicCount`
               （有单测），这里只排版；`counts === null` 时**整块不渲染**（不画一堆 0）。
            ⚠️ 2026-10-20 用户改了口径（只改**这个蓝底块**）：
               "那节多音字后面，**你那节拼音就不要列出来了，只需要列出字就可以了**。"
               所以清单改用 `describePolyphonicChars()`（`地、数`，**不带读音**）；
               重复明细（`DuplicateReport`）与「整块导入（高级）」面板**不动**。
          */}
          {report.summary.counts !== null && (
            <div className="mt-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2">
              <p className="text-sm font-semibold text-slate-800">
                {describeDistinctTotal(report.summary.counts)}
              </p>
              <p className="mt-0.5 text-xs text-slate-600">
                {describePolyphonicCount(report.summary.counts)}
                {report.summary.counts.polyphonicChars > 0 && (
                  <>
                    {' —— '}
                    <span className="font-mono text-slate-800">
                      {report.duplicates === null
                        ? '明细没拿到'
                        : describePolyphonicChars(report.duplicates.polyphonic) || '明细没拿到'}
                    </span>
                  </>
                )}
              </p>
            </div>
          )}

          {/*
            ---------- **列出这次要导入的字**（迁移 0018，2026-10-19；10-20 第三/七轮）----------
            ⚠️ 用与「浏览与编辑」**同一份** `HanziCard` 网格（手机 3 / 桌面 8 列）；
               底色：重复未标音 = 浅玫瑰 / 重复已标音 = **浅绿** / 没重复 = 白 /
               文件里标了「?」还没确认 = **浅橙**（点开确认后变浅绿，见 `HanziCard`）；
               卡片上**显示拼音**（没标音显示库里的默认读音）—— 拼音就是核对依据。
            ⚠️ 点字 → 读这个位置的读音 → 父组件重新预览；保存前再校验一遍。
          */}
          {!isWordList && report.readings !== null && (
            <ImportListingPreview
              report={report}
              busy={readingsBusy || previewing}
              onPickReading={(line, index, reading) => onPickReading?.(line, index, reading)}
            />
          )}

          {/*
            ---------- **词语表：这次要导入的词（词卡网格）**（2026-10-23）----------
            背景：词语表预览以前**一个词都不列**（上面那张字卡网格被 `!isWordList` 挡住），
            用户导入时只看见数字与重复明细，**看起来一片空白**，没有可核对的东西。
            现在补一张**只展示**的词卡网格：
              · **拼音在上、词在下**（与字卡同一种排法，用户明确要求）；
              · 拼音是**整词多音节**（`kàn jiàn`）；
              · 一行 = 一块：`第 N 行（X 个词）`，与字表一样按粘贴的行分组；
              · 手机 2 / 小屏 3 / 桌面 4 列（词比字宽）；
              · 词再多也**一次全显示**（225 个也一样），不分页。
            ⛔ 卡片只展示：没有浮窗、没有跳转、没有朗读（用户点名不要）。
            ⛔ 没有把 2026-10-21 删掉的那两块加回来（「会新增的 N 条（点开看逐行）」、
               「⚠ 有问题的行（N）」的逐条明细）—— 它们与本网格无关。
            ⚠️ 字表的预览**一个字都没动**：这里只在 `cibiao` 时多渲染这一块。
          */}
          {isWordList && (
            <WordCardGrid
              report={report}
              text={text}
              defaultTextbook={activeTextbook?.id ?? ''}
            />
          )}

          {/*
            ---------- 重复明细（哪几行、哪个字/词、会怎么处理） ----------
            ⚠️ 用户原话："如果有重复的字或词，但有提示，**几行，什么字或词**。"
              改造前这里只有上面那格数字（"同一份里重复 1 条"）—— 粘了 11 行、
              里面「上」出现两次的人不知道是哪个字、在哪两行。
            ⚠️ 数据**全部来自后端的 `report.duplicates`**（shared 纯函数算的），
              这个面板不解析文本、不数行号。
            ⚠️ 它是**提示**不是错误：不拦「确认导入」，所以放在统计下面、错误列表上面。
          */}
          <DuplicateReport
            duplicates={report.duplicates}
            mergedCount={report.summary.counts?.merged ?? null}
            polyphonicCount={report.summary.counts?.mergedPolyphonic ?? null}
          />

          {commit?.links != null && (
            <p className="mt-2 rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-700">
              写入的关联：字 ↔ 词 {countLabel(commit.links.wordHanzi)} 条、收录{' '}
              {countLabel(commit.links.hanziListing)} 条、字 ↔ 课文{' '}
              {countLabel(commit.links.hanziLesson)} 条、词 ↔ 课文{' '}
              {countLabel(commit.links.wordLesson)} 条。
            </p>
          )}

          {/*
            ---------- 逐字序号（2026-10-19）----------
            ⚠️ 2026-10-21 用户要求删掉「字表顺序：N 个字按粘贴顺序编号…」那一行
               （"这些说明文字块看不过来"）；`report.listingOrder` 数据与后端计算
               **原样保留**，只是预览面板不再渲染这段说明。
          */}

          {/*
            ---------- 有问题的行（`report.errors`）—— **2026-10-21 本轮整块已删** ----------
            ⚠️ 用户原话："**01 图片中的两块都要去掉，不要！**" —— 截图里那块
               「⚠ 有问题的行（2）」的两个条目就是三道校验的**汇总句**：
                 · 「第 1 行有 46 个字的读音需要你确认：…，确认之后才能保存。」
                 · 「第 13 行有 3 处「同一个字标了同样的音」，不能重复录入：…」
               它们与字卡区（`ImportListingPreview`）里那三道校验说的是同一件事，
               用户明确要删，所以**整块不再渲染**（连标题、连空壳一起）。
            ⚠️ 接口层**没有改**：`report.errors` 照旧返回、`summary.errors` 照旧计数
               （统计格「有问题的行」与「确认导入」那句仍然用它）；这里只是不再画那块。
          */}

          {report.skipped !== null && report.skipped.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm font-medium text-slate-700">
                已存在、跳过的 {report.skipped.length} 条（重复导入就是这里）
              </summary>
              <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto pr-1 text-sm">
                {report.skipped.map((row, index) => (
                  <li
                    key={`${row.line}-${index}`}
                    className="flex min-w-0 gap-2 rounded-lg bg-slate-50 px-2 py-1"
                  >
                    <span className="shrink-0 tabular-nums text-slate-400">第 {row.line} 行</span>
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-800">
                      {row.text}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Card>
      )}

      {preview !== null && commit === null && (
        <Card>
          <CardHeader
            title="确认导入"
            description={`会新增 ${countLabel(preview.summary.created)} 条、更新 ${countLabel(
              preview.summary.updated,
            )} 条、跳过 ${countLabel(preview.summary.skipped)} 条；有问题的行 ${countLabel(
              preview.summary.errors,
            )} 行不会写入。`}
          />
          {/*
            ⚠️ 2026-10-21 用户要求删掉「还不能保存：有 N 个字的读音需要你确认…」那段；
               校验逻辑**一个字都没动**（`readings.ok` 仍然控制按钮禁用，
               `ImportListingPreview` 里仍然逐条列出没标音 / 待确认 / 同字同音的红字）。
          */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              loading={committing}
              disabled={!canCommit}
              onClick={() => onCommit?.()}
              icon={<IconCheck size={18} />}
            >
              确认导入
            </Button>
            <span className="text-xs text-slate-500">
              预览之后改过上面的内容，请重新预览（提交用的必须是你看过的那一份）。
            </span>
          </div>
          {commitError !== null && (
            <p className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              导入失败：{commitError}
            </p>
          )}
        </Card>
      )}

      {commit !== null && (
        <Card>
          <CardHeader
            title="录完了"
            description={`这次写入了 ${countLabel(commit.summary.created)} 条新增、${countLabel(
              commit.summary.updated,
            )} 条更新，跳过 ${countLabel(commit.summary.skipped)} 条。`}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => onReset?.()}>
              接着录别的表
            </Button>
            <span className="text-xs text-slate-500">
              同一个字可以再去另一张表上录一遍（识字表 + 写字表各一条，互不覆盖）。
            </span>
          </div>
        </Card>
      )}

      {loading && textbooks === null && (
        <div className="space-y-2">
          <Skeleton className="h-24 w-full" />
        </div>
      )}
    </div>
  );
}
